<template>
  <div v-if="hasVisibleContent" class="relative px-4 md:px-5 h-full w-full mt-2">
    <div class="py-2 max-w-2xl mx-auto w-full">
      <div class="flex flex-col" :class="compact ? 'gap-1.5' : 'gap-3 md:gap-4'">
        <template v-if="displayPricingTiers.length > 0">
          <div
            class="flex min-w-0 w-full"
            :class="compact ? 'flex-col gap-3' : 'flex-col md:flex-row gap-3 md:gap-4 md:items-center md:px-6'"
          >
            <div
              v-if="!compact"
              class="hidden md:block text-sm text-gray-brand-700 order-first md:order-none max-w-[14rem] text-center"
            >
              <template v-if="showNextTierText">
                {{ progressRemainingParts.before }}
                <span class="font-semibold text-brand-600">{{ progressRemainingParts.highlight }}</span>
                {{ progressRemainingParts.after }} for {{ nextPricingTier?.attributes?.title }}
              </template>
              <template v-else-if="pricingTier && !pricingTier.attributes.is_default">
                You've unlocked {{ pricingTier?.attributes?.title }}!
              </template>
            </div>

            <div class="flex-1 flex flex-col gap-0.5 min-w-0 pr-10" :class="{ 'md:pr-8 md:mb-1': !compact }">
              <div class="relative h-3 leading-tight overflow-visible">
                <div
                  class="absolute inset-y-0 left-0 flex transition-transform duration-500 ease-out"
                  :style="{
                    width: scrollWrapperWidth,
                    transform: scrollTransform,
                    transitionDelay: needsScrolling ? '0.4s' : '0s'
                  }"
                >
                  <div
                    v-for="(tier, index) in displayPricingTiers"
                    :key="tier.id"
                    class="relative overflow-visible"
                    :class="{ 'flex-1': !needsScrolling }"
                    :style="segmentWidth ? { width: segmentWidth } : {}"
                  >
                    <span
                      v-if="tierHasDiscount(tier) || shouldShowFreeShippingLabel(tier)"
                      class="absolute right-0 bottom-0 flex items-center gap-0.5 text-xs font-medium whitespace-nowrap leading-none z-10 transition-opacity duration-300"
                      :class="isLabelVisible(index) ? 'opacity-100' : 'opacity-0 pointer-events-none'"
                      style="transform: translateX(50%)"
                    >
                      <span
                        v-if="tierHasDiscount(tier)"
                        class="transition-colors duration-300"
                        :class="isCurrentTier(tier) ? 'text-brand-600' : 'text-gray-brand-600'"
                      >
                        {{ tierDiscountLabel(tier) }} Off
                      </span>
                      <span
                        v-if="tierHasDiscount(tier) && shouldShowFreeShippingLabel(tier)"
                        class="transition-colors duration-300"
                        :class="isCurrentTier(tier) ? 'text-brand-600' : 'text-gray-brand-600'"
                      >
                        +
                      </span>
                      <span
                        v-if="shouldShowFreeShippingLabel(tier)"
                        class="relative group cursor-pointer p-2 -m-2 transition-colors duration-300"
                        :class="isCurrentTier(tier) ? 'text-brand-600' : 'text-gray-brand-600'"
                      >
                        <TruckIcon class="w-4 h-4" />
                        <span
                          class="absolute bottom-full left-1/2 -translate-x-1/2 mb-1 px-2 py-1 text-xs text-white bg-gray-800 rounded whitespace-nowrap opacity-0 group-hover:opacity-100 pointer-events-none transition-opacity"
                        >
                          Free Delivery / Shipping
                        </span>
                      </span>
                    </span>
                  </div>
                </div>
              </div>
              <div class="relative h-2.5 bg-gray-brand-200 overflow-hidden rounded-full">
                <div
                  class="absolute inset-y-0 left-0 rounded-full transition-all duration-300 ease-out"
                  :style="{ width: totalFillWidth, background: gradientStyle }"
                />
              </div>
            </div>

            <div v-if="!compact" class="flex justify-center md:flex-shrink-0">
              <slot />
            </div>
          </div>

          <div class="text-sm text-gray-brand-700" :class="compact ? 'text-left' : 'text-center md:px-6 md:hidden'">
            <template v-if="showNextTierText">
              {{ progressRemainingParts.before }}
              <span class="font-semibold text-brand-600">{{ progressRemainingParts.highlight }}</span>
              {{ progressRemainingParts.after }} for {{ nextPricingTier?.attributes?.title }}
            </template>
            <template v-else-if="pricingTier && !pricingTier.attributes.is_default">
              You've unlocked {{ pricingTier?.attributes?.title }}!
            </template>
          </div>
        </template>
      </div>
    </div>
  </div>
</template>

<script>
import { tierHasDiscount, tierDiscountLabel } from '@/utils/pricingTierDiscount'
import { TruckIcon } from '@heroicons/vue/outline'

// Matches the scroll wrapper's transition-delay (0.4s) + duration-500 (0.5s) below.
// Keep these in sync — diverging the values re-introduces the right-edge label flash.
const SCROLL_ANIMATION_MS = 900

export default {
  name: 'PricingTierProgressBar',
  components: { TruckIcon },
  props: {
    // Always render the stacked mobile layout (bar full-width, text below) —
    // for narrow fixed-width containers like the cart drawer, where the
    // viewport-based md: layout would collide the tier labels.
    compact: { type: Boolean, default: false }
  },
  data() {
    return {
      delayedScrollOffset: 0
    }
  },
  mounted() {
    this.delayedScrollOffset = this.scrollOffset
  },
  beforeUnmount() {
    if (this._scrollTimer) clearTimeout(this._scrollTimer)
  },
  watch: {
    scrollOffset(newVal) {
      if (this._scrollTimer) clearTimeout(this._scrollTimer)
      this._scrollTimer = setTimeout(() => {
        this.delayedScrollOffset = newVal
      }, SCROLL_ANIMATION_MS)
    }
  },
  computed: {
    cart() {
      return this.$store.getters.currentCart
    },
    availablePricingTiers() {
      return this.$store.getters.availablePricingTiers
    },
    nonDefaultPricingTiers() {
      return this.availablePricingTiers?.filter((t) => !t.attributes.is_default) || []
    },
    displayPricingTiers() {
      return this.nonDefaultPricingTiers
    },
    currentUnlockedTier() {
      for (let i = this.displayPricingTiers.length - 1; i >= 0; i--) {
        const tier = this.displayPricingTiers[i]
        if (this.currentProgressValue >= this.getTierMinimum(tier)) {
          return tier
        }
      }
      return null
    },
    hasVisibleContent() {
      return this.displayPricingTiers.length > 0
    },
    pricingTierID() {
      return this.cart?.relationships?.pricing_tier?.data?.id
    },
    pricingTier() {
      return this.$store.getters.pricingTierWithID(this.pricingTierID)
    },
    nextPricingTierID() {
      return this.cart?.relationships?.next_pricing_tier?.data?.id
    },
    nextPricingTier() {
      return this.$store.getters.pricingTierWithID(this.nextPricingTierID)
    },
    progressRemainingParts() {
      const text = this.cart?.attributes?.progress_remaining_language_next_pricing_tier || ''
      const match = text.match(/^(.*?)([$]?\d[\d.,]*\s+\S+)(.*)$/)
      if (match) {
        return { before: match[1], highlight: match[2], after: match[3] }
      }
      return { before: '', highlight: text, after: '' }
    },
    isPriceBased() {
      // Decided across the whole ladder rather than off the first tier alone. A leading tier with no
      // minimum — an unconditional discount, which merchants legitimately configure — says nothing
      // about how the ladder is measured, and reading the mode from it flipped a price ladder into
      // item-count mode where every minimum is 0. Mirrors the backend's Store#has_mixed_pricing_tiers?.
      const tiers = this.nonDefaultPricingTiers
      const anyPriceThreshold = tiers.some((t) => (t.attributes?.minimum_price?.cents || 0) > 0)
      const anyItemThreshold = tiers.some((t) => (t.attributes?.minimum_item_count || 0) > 0)
      return anyPriceThreshold && !anyItemThreshold
    },
    currentProgressValue() {
      if (this.isPriceBased) {
        return this.cart?.attributes?.total_price_subject_to_pricing_tiers?.cents || 0
      }
      return this.cart?.attributes?.quantity_subject_to_pricing_tiers ?? this.cart?.attributes?.quantity ?? 0
    },
    totalFillWidth() {
      const total = this.displayPricingTiers.length
      if (total === 0) return '0%'

      // Each visible segment occupies 1/maxVisibleSegments of the bar container,
      // so we sum each tier's progress contribution in container-width units and
      // then shift left by scrollOffset to match any segments scrolled off-screen.
      // This keeps the fill aligned with the tier labels in both the 3-visible
      // and the 4+ (scrolling) layouts.
      const segmentsInContainer = this.maxVisibleSegments
      let filledFraction = 0

      for (let i = 0; i < total; i++) {
        const tier = this.displayPricingTiers[i]
        const tierMinimum = this.getTierMinimum(tier)
        const previousTierMinimum = i > 0 ? this.getTierMinimum(this.displayPricingTiers[i - 1]) : 0
        const rangeSize = tierMinimum - previousTierMinimum
        const progressInRange = this.currentProgressValue - previousTierMinimum

        // A zero-width range — a tier with no minimum, or one sharing the threshold below it — is
        // filled only once the cart has actually reached that threshold. Treating it as
        // unconditionally complete reported the entire ladder as earned on an empty cart.
        const segmentProgress =
          rangeSize <= 0
            ? Number(this.currentProgressValue >= tierMinimum)
            : Math.min(1, Math.max(0, progressInRange / rangeSize))

        filledFraction += segmentProgress / segmentsInContainer
      }

      filledFraction -= this.scrollOffset / segmentsInContainer

      return Math.max(0, Math.min(1, filledFraction)) * 100 + '%'
    },
    gradientStyle() {
      return 'linear-gradient(to right, hsl(var(--main-brand-hue), var(--main-brand-saturation-200), var(--main-brand-lightness-200)), hsl(var(--main-brand-hue), var(--main-brand-saturation-500), var(--main-brand-lightness-500)))'
    },
    totalSegments() {
      return this.displayPricingTiers.length
    },
    maxVisibleSegments() {
      return Math.min(3, this.totalSegments)
    },
    needsScrolling() {
      return this.totalSegments > 3
    },
    currentTierIndex() {
      const currentValue = this.currentProgressValue
      for (let i = this.displayPricingTiers.length - 1; i >= 0; i--) {
        const tier = this.displayPricingTiers[i]
        const tierMinimum = this.getTierMinimum(tier)
        if (currentValue >= tierMinimum) {
          return i
        }
      }
      return 0
    },
    scrollOffset() {
      if (!this.needsScrolling) return 0
      const offset = this.currentTierIndex - 1
      const maxOffset = this.totalSegments - this.maxVisibleSegments
      return Math.max(0, Math.min(offset, maxOffset))
    },
    scrollWrapperWidth() {
      if (!this.needsScrolling) return '100%'
      const widthPercent = (this.totalSegments / this.maxVisibleSegments) * 100
      return `${widthPercent}%`
    },
    segmentWidth() {
      if (!this.needsScrolling) return null
      return `${100 / this.totalSegments}%`
    },
    scrollTransform() {
      if (!this.needsScrolling) return 'translateX(0)'
      const offsetPercent = this.scrollOffset * (100 / this.totalSegments)
      return `translateX(-${offsetPercent}%)`
    },
    showNextTierText() {
      return !!this.nextPricingTier
    }
  },
  methods: {
    getTierMinimum(tier) {
      if (this.isPriceBased) {
        return tier.attributes.minimum_price?.cents || 0
      }
      return tier.attributes.minimum_item_count || 0
    },
    isCurrentTier(tier) {
      return this.currentUnlockedTier?.id === tier.id
    },
    shouldShowFreeShippingLabel(tier) {
      return !!tier.attributes.free_shipping
    },
    tierHasDiscount(tier) {
      return tierHasDiscount(tier)
    },
    tierDiscountLabel(tier) {
      return tierDiscountLabel(tier)
    },
    isLabelVisible(index) {
      if (!this.needsScrolling) return true
      // Intersection of the current and just-completed scroll windows: a tier
      // newly entering on the trailing edge stays hidden until the wrapper has
      // finished translating, then fades in at its final position.
      const start = Math.max(this.scrollOffset, this.delayedScrollOffset)
      const end = Math.min(this.scrollOffset, this.delayedScrollOffset) + this.maxVisibleSegments
      return index >= start && index < end
    }
  }
}
</script>
